import { type ChannelsAction } from "@dashboard/channels/urls";
import { Condition } from "@dashboard/components/ConditionalFilter/FilterElement/Condition";
import {
  type ConditionItem,
  ConditionOptions,
} from "@dashboard/components/ConditionalFilter/FilterElement/ConditionOptions";
import { ConditionSelected } from "@dashboard/components/ConditionalFilter/FilterElement/ConditionSelected";
import {
  ExpressionValue,
  FilterElement,
} from "@dashboard/components/ConditionalFilter/FilterElement/FilterElement";
import { prepareStructure } from "@dashboard/components/ConditionalFilter/ValueProvider/utils";
import { stringify } from "qs";
import urlJoin from "url-join";

import {
  type ActiveTab,
  type BulkAction,
  type Dialog,
  type Filters,
  type FiltersAsDictWithMultipleValues,
  type FiltersWithKeyValueValues,
  type FiltersWithMultipleValues,
  type Pagination,
  type SingleAction,
  type Sort,
  type TabActionDialog,
} from "../types";
import { stringifyQs } from "../utils/urls";

const productSection = "/products/";

export const productAddPath = urlJoin(productSection, "add");
export const productAddUrl = (params?: ProductCreateUrlQueryParams) =>
  productAddPath + "?" + stringifyQs(params);

export const productListPath = productSection;
// copy: 목록의 '복사' 버튼이 여는 확인창 (2026-09-04 KST 추가)
export type ProductListUrlDialog =
  | "delete"
  | "export"
  | "create-product"
  | "copy"
  | TabActionDialog;
export enum ProductListUrlFiltersEnum {
  priceFrom = "priceFrom",
  priceTo = "priceTo",
  status = "status",
  stockStatus = "stockStatus",
  query = "query",
  channel = "channel",
  productKind = "productKind",
}
export enum ProductListUrlFiltersWithMultipleValues {
  categories = "categories",
  collections = "collections",
  productTypes = "productTypes",
}
export const ProductListUrlFiltersAsDictWithMultipleValues = {
  booleanAttributes: "boolean-attributes",
  dateAttributes: "date-attributes",
  dateTimeAttributes: "datetime-attributes",
  numericAttributes: "numeric-attributes",
  stringAttributes: "string-attributes",
} as const;
export type ProductListUrlFiltersAsDictWithMultipleValues =
  (typeof ProductListUrlFiltersAsDictWithMultipleValues)[keyof typeof ProductListUrlFiltersAsDictWithMultipleValues];
export enum ProductListUrlFiltersWithKeyValueValues {
  metadata = "metadata",
}
export type ProductListUrlFilters = Filters<ProductListUrlFiltersEnum> &
  FiltersWithMultipleValues<ProductListUrlFiltersWithMultipleValues> &
  FiltersWithKeyValueValues<ProductListUrlFiltersWithKeyValueValues> &
  FiltersAsDictWithMultipleValues<ProductListUrlFiltersAsDictWithMultipleValues>;
export enum ProductListUrlSortField {
  attribute = "attribute",
  name = "name",
  productType = "productType",
  availability = "availability",
  price = "price",
  rank = "rank",
  date = "date",
  created = "created",
  // 찜 많은순 (2026-10-26 KST 추가)
  wishCount = "wishCount",
}
type ProductListUrlSort = Sort<ProductListUrlSortField>;
export interface ProductListUrlQueryParams
  extends BulkAction,
    // SingleAction: 복사 확인창이 대상 상품 하나를 id 로 물고 간다 (2026-09-04 KST 추가)
    SingleAction,
    Dialog<ProductListUrlDialog>,
    ProductListUrlFilters,
    ProductListUrlSort,
    Pagination,
    ActiveTab {
  attributeId?: string;
  presestesChanged?: string;
}
export const productListUrl = (params?: ProductListUrlQueryParams): string =>
  productListPath + "?" + stringifyQs(params);

/**
 * Creates a product type filter element using the conditional filter system.
 * The product list encodes filters as URL tokens (field slug + product type slug), not legacy query params.
 */
const createProductTypeFilterElement = (productType: {
  id: string;
  name: string;
  slug: string;
}): FilterElement => {
  const expressionValue = new ExpressionValue("productType", "ProductType", "productType");
  const conditionOptions = ConditionOptions.fromStaticElementName("productType");
  const conditionItem: ConditionItem = { type: "combobox", label: "is", value: "input-1" };
  const conditionSelected = ConditionSelected.fromConditionItemAndValue(conditionItem, {
    label: productType.name,
    value: productType.id,
    slug: productType.slug,
  });
  const condition = new Condition(conditionOptions, conditionSelected, false);

  return new FilterElement(expressionValue, condition, false);
};

/**
 * Builds the product list URL pre-filtered by a single product type.
 */
export const productListUrlWithProductType = (productType?: {
  id: string;
  name: string;
  slug: string;
}) => {
  if (!productType?.id || !productType.slug) {
    return productListPath;
  }

  const filterContainer = [createProductTypeFilterElement(productType)];
  const queryParams = prepareStructure(filterContainer);

  return urlJoin(productListPath, "?" + stringify(queryParams));
};

// 삭제 보관함 — 상품 ID 경로(/products/:id)보다 먼저 라우팅해야 한다 (2026-10-14 KST 추가)
export const productTrashPath = urlJoin(productSection, "deleted");
export type ProductTrashUrlQueryParams = Pagination;
export const productTrashUrl = (params?: ProductTrashUrlQueryParams) =>
  productTrashPath + "?" + stringifyQs(params);

// 기본정보 관리 — 삭제 보관함처럼 상품 ID 경로보다 먼저 라우팅한다 (2026-10-27 KST 추가)
export const productBasicInfoPath = urlJoin(productSection, "basic-info");
// 정렬은 상품 목록과 같은 필드를 쓴다 (2026-10-27 KST 수정)
export type ProductBasicInfoUrlQueryParams = Pagination & ProductListUrlSort;
export const productBasicInfoUrl = (params?: ProductBasicInfoUrlQueryParams) =>
  productBasicInfoPath + "?" + stringifyQs(params);

// 가격 관리 — 기본정보 관리처럼 상품 ID 경로보다 먼저 라우팅한다 (2026-10-27 KST 추가)
export const productPricePath = urlJoin(productSection, "pricing");
export type ProductPriceUrlQueryParams = Pagination & ProductListUrlSort;
export const productPriceUrl = (params?: ProductPriceUrlQueryParams) =>
  productPricePath + "?" + stringifyQs(params);

export const productPath = (id: string) => urlJoin(productSection + id);
export type ProductUrlDialog =
  | "remove"
  | "remove-media"
  | "assign-attribute-value"
  | "view-metadata"
  | ChannelsAction;
export type ProductUrlQueryParams = BulkAction & Dialog<ProductUrlDialog> & SingleAction;
export type ProductCreateUrlDialog = "assign-attribute-value" | ChannelsAction;
interface ProductCreateUrlProductType {
  "product-type-id"?: string;
}
export type ProductCreateUrlQueryParams = Dialog<ProductCreateUrlDialog> &
  SingleAction &
  ProductCreateUrlProductType;
export const productUrl = (id: string, params?: ProductUrlQueryParams) =>
  productPath(encodeURIComponent(id)) + "?" + stringifyQs(params);

export const productVariantEditPath = (variantId: string) =>
  urlJoin(productSection, "variant", variantId);

/** @deprecated TODO: Remove in Saleor Dashboard 3.23 */
export const productVariantLegacyEditPath = (productId: string, variantId: string) =>
  urlJoin(productSection, productId, "variant", variantId);

export type ProductVariantEditUrlDialog = "remove" | "assign-attribute-value" | "view-metadata";
export type ProductVariantEditUrlQueryParams = Dialog<ProductVariantEditUrlDialog> & SingleAction;
export const productVariantEditUrl = (
  variantId: string,
  params?: ProductVariantEditUrlQueryParams,
) => productVariantEditPath(encodeURIComponent(variantId)) + "?" + stringifyQs(params);

type ProductVariantAddUrlDialog = "assign-attribute-value";
export type ProductVariantAddUrlQueryParams = Dialog<ProductVariantAddUrlDialog> & SingleAction;
export const productVariantAddPath = (productId: string) =>
  urlJoin(productSection, productId, "variant/add");
export const productVariantAddUrl = (
  productId: string,
  params?: ProductVariantAddUrlQueryParams,
): string => productVariantAddPath(encodeURIComponent(productId)) + "?" + stringifyQs(params);

export const productImagePath = (productId: string, imageId: string) =>
  urlJoin(productSection, productId, "image", imageId);
export type ProductImageUrlDialog = "remove" | "view-metadata";
export type ProductImageUrlQueryParams = Dialog<ProductImageUrlDialog>;
export const productImageUrl = (
  productId: string,
  imageId: string,
  params?: ProductImageUrlQueryParams,
) =>
  productImagePath(encodeURIComponent(productId), encodeURIComponent(imageId)) +
  "?" +
  stringifyQs(params);
